/* eslint-disable max-lines */
import { ColorPalette } from 'models/style';
import React, { ComponentProps, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components/macro';
import { defaultDelay } from 'utils/configs';
import { fontSizePalette, fontWeightPalette, sizePalette } from 'utils/styleConstants';
import Icon, { IconNames } from '../Input/Icon';
import { useTheme } from 'providers/ThemeProvider';

export type ButtonScope = 'primary' | 'secondary' | 'tertiary' | 'quaternary';
interface CommonStyleButton {
  icon: boolean;
  scope: ButtonScope;
  noPadding?: boolean;
  selected: boolean;
  customFixedWidth?: string;
  disabledColor?: string;
  iconColor?: string;
}
export interface Props extends ComponentProps<'button'> {
  id: string;
  selected?: boolean;
  scope?: ButtonScope;
  action?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  white?: boolean;
  customIcon?: IconNames;
  noPadding?: boolean;
  customFixedWidth?: string;
  tooltip?: string;
  iconColor?: string;
  useThrottle?: boolean;
  disabledColor?: string;
  iconSize?: any;
}

const createCustomStyledButton = {
  primary: (selected: boolean, colorPalette: ColorPalette) => css`
    background: ${selected
      ? colorPalette.isDarkMode
        ? colorPalette.turquoise.normal
        : colorPalette.turquoise.normal
      : colorPalette.isDarkMode
      ? colorPalette.turquoise.dark
      : colorPalette.turquoise.dark};
    color: ${selected
      ? colorPalette.isDarkMode
        ? colorPalette.black
        : colorPalette.white
      : colorPalette.isDarkMode
      ? colorPalette.black
      : colorPalette.white};
    border: solid ${sizePalette.borderWidth.DEFAULT} transparent;
    &:disabled {
      background-color: ${colorPalette.isDarkMode
        ? colorPalette.turquoise.softTurquoise
        : colorPalette.turquoise.softTurquoise};
      color: ${colorPalette.isDarkMode ? colorPalette.grey.grey7 : colorPalette.white};
    }
  `,
  secondary: (selected: boolean, colorPalette: ColorPalette) => css`
    color: ${selected
      ? colorPalette.isDarkMode
        ? colorPalette.turquoise.dark
        : colorPalette.turquoise.normal
      : colorPalette.isDarkMode
      ? colorPalette.turquoise.light
      : colorPalette.turquoise.normal};
    border: solid ${sizePalette.borderWidth.DEFAULT};
    border-color: ${selected
      ? colorPalette.isDarkMode
        ? colorPalette.turquoise.accent
        : colorPalette.turquoise.accent2
      : colorPalette.isDarkMode
      ? colorPalette.black
      : colorPalette.grey.grey5};
    background: ${selected
      ? colorPalette.isDarkMode
        ? colorPalette.turquoise.accent
        : colorPalette.turquoise.accent2
      : colorPalette.isDarkMode
      ? 'transparent'
      : colorPalette.white};
    &:disabled {
      cursor: default;
      color: ${colorPalette.isDarkMode ? colorPalette.grey.grey8 : colorPalette.turquoise.dark};
      border: ${sizePalette.borderWidth.DEFAULT} solid;
      border-color: ${colorPalette.isDarkMode ? colorPalette.grey.grey8 : colorPalette.grey.grey3};
      background-color: ${colorPalette.isDarkMode ? 'inherit' : colorPalette.grey.grey3};
    }
  `,

  tertiary: (selected: boolean, colorPalette: ColorPalette) => css`
    background: transparent;
    color: ${selected
      ? colorPalette.isDarkMode
        ? colorPalette.turquoise.normal
        : colorPalette.grey.grey9
      : colorPalette.isDarkMode
      ? colorPalette.turquoise.accent
      : colorPalette.turquoise.normal};

    text-decoration: underline;
    &:disabled {
      text-decoration: none;
      cursor: default;
      color: ${colorPalette.isDarkMode ? colorPalette.grey.grey8 : colorPalette.turquoise.dark};
      opacity: ${colorPalette.isDarkMode ? 1 : 0.5};
    }
  `,

  quaternary: (selected: boolean, colorPalette: ColorPalette) => css`
    color: ${selected
      ? colorPalette.isDarkMode
        ? colorPalette.grey.grey10
        : colorPalette.turquoise.dark
      : colorPalette.isDarkMode
      ? colorPalette.grey.grey10
      : colorPalette.turquoise.normal};
    border: solid ${sizePalette.borderWidth.DEFAULT};
    border-color: ${selected
      ? colorPalette.isDarkMode
        ? colorPalette.grey.grey5
        : colorPalette.turquoise.dark
      : colorPalette.isDarkMode
      ? colorPalette.white
      : colorPalette.turquoise.normal};
    background: ${selected
      ? colorPalette.isDarkMode
        ? colorPalette.grey.grey5
        : 'transparent'
      : colorPalette.isDarkMode
      ? colorPalette.white
      : 'transparent'};

    &:disabled {
      cursor: default;
      color: ${colorPalette.isDarkMode ? colorPalette.grey.grey8 : colorPalette.grey.grey5};
      border: ${sizePalette.borderWidth.DEFAULT} solid;
      border-color: ${colorPalette.isDarkMode ? colorPalette.white : colorPalette.grey.grey5};
    }
  `,
};

export const CommonStyledButton = styled.button<CommonStyleButton>`
  font-family: ${(props) => props.theme.fontFamily};
  padding: ${(props: CommonStyleButton) =>
    props.scope === 'secondary' && props.icon && props.noPadding
      ? sizePalette.spacing.px
      : `0 ${sizePalette.spacing[2]}`};
  border: solid ${sizePalette.borderWidth.DEFAULT} transparent;
  background: transparent;
  border-radius: ${(props) =>
    props.scope === 'secondary' && props.icon && props.noPadding
      ? sizePalette.borderRadius.full
      : sizePalette.borderRadius['2xl']};
  font-size: ${fontSizePalette.medium};
  display: flex !important;
  align-items: center;
  justify-content: center;
  letter-spacing: ${sizePalette.letterSpacing.normal};
  font-weight: ${fontWeightPalette.regular};
  line-height: ${sizePalette.lineHeight[5]};
  cursor: pointer;
  white-space: nowrap;
  width: ${(props) =>
    props?.customFixedWidth ||
    (props.scope === 'secondary' && props.icon && props.noPadding ? sizePalette.spacing.pxToRem(30) : 'auto')};
  height: ${sizePalette.spacing.pxToRem(30)};

  &:disabled {
    cursor: default;
    ${(props) => props.disabledColor && `background: ${props.disabledColor} !important`};
  }

  ${({ scope, selected, theme }) => createCustomStyledButton[scope](selected, theme.colorPalette)}

  svg {
    margin-right: ${(props) => (props.noPadding ? sizePalette.spacing[0] : sizePalette.spacing[1])};
    margin-left: 0;
    padding-left: 0;
    ${(props) => (props.iconColor ? `color: ${props.iconColor} !important;` : '')}
  }
`;

const DivImg = styled.div<{ iconColor?: string; disabled?: boolean }>`
  display: inline-block;
`;

const getButtonColor = (scope: ButtonScope, selected: boolean, colorPalette: ColorPalette, disabled?: boolean) => {
  if (disabled) {
    if (colorPalette.isDarkMode) {
      switch (scope) {
        case 'primary':
          return colorPalette.grey.grey8;
        case 'tertiary':
        case 'secondary':
        case 'quaternary':
          return colorPalette.grey.grey8;
      }
    }
    switch (scope) {
      case 'primary':
        return colorPalette.white;
      case 'tertiary':
        return colorPalette.turquoise.dark;

      case 'secondary':
      case 'quaternary':
        return colorPalette.grey.grey8;
    }
  }
  switch (scope) {
    case 'primary':
      return selected
        ? colorPalette.isDarkMode
          ? colorPalette.black
          : colorPalette.white
        : colorPalette.isDarkMode
        ? colorPalette.black
        : colorPalette.white;
    case 'secondary':
      return selected
        ? colorPalette.isDarkMode
          ? colorPalette.turquoise.dark
          : colorPalette.turquoise.normal
        : colorPalette.isDarkMode
        ? colorPalette.turquoise.light
        : colorPalette.turquoise.normal;
    case 'tertiary':
      return selected
        ? colorPalette.isDarkMode
          ? colorPalette.turquoise.normal
          : colorPalette.grey.grey9
        : colorPalette.isDarkMode
        ? colorPalette.turquoise.accent
        : colorPalette.turquoise.normal;
    case 'quaternary':
      return selected
        ? colorPalette.isDarkMode
          ? colorPalette.grey.grey10
          : colorPalette.turquoise.dark
        : colorPalette.isDarkMode
        ? colorPalette.grey.grey10
        : colorPalette.turquoise.normal;
    default:
      return colorPalette.black;
  }
};

const CommonButton = (props: Props) => {
  const {
    type,
    value,
    id,
    disabled,
    selected = false,
    scope = 'primary',
    action,
    noPadding,
    customIcon,
    customFixedWidth,
    tooltip,
    iconColor,
    useThrottle = true,
    disabledColor,
    iconSize = scope === 'secondary' ? '18px' : '20px',
    style,
  } = props;

  const [throttle, setThrottle] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (useThrottle) {
      setThrottle(true);
      if (throttle === false) {
        action?.(e);
      }
      timeoutRef.current = setTimeout(() => {
        setThrottle(false);
      }, defaultDelay);
    } else {
      action?.(e);
    }
  };

  const hasIcon = !!customIcon;
  const noPaddingMemo = useMemo(() => {
    return !value ? true : noPadding;
  }, [value, noPadding]);
  const { theme } = useTheme();
  const buttonColor = getButtonColor(scope, selected, theme.colorPalette, disabled);

  return (
    <CommonStyledButton
      title={tooltip}
      customFixedWidth={customFixedWidth}
      id={id}
      data-walkthrough={id}
      type={type}
      value={value}
      onClick={handleClick}
      noPadding={noPaddingMemo}
      selected={selected}
      icon={hasIcon ? true : false}
      scope={scope}
      disabled={disabled !== undefined ? disabled : useThrottle ? throttle : undefined}
      disabledColor={disabledColor}
      style={style}
      iconColor={iconColor ?? buttonColor}
    >
      <DivImg iconColor={iconColor ?? buttonColor} disabled={disabled}>
        {customIcon && (
          <Icon
            icon={customIcon}
            disabled={iconColor ? disabled : undefined}
            size={iconSize}
            style={{
              color: iconColor ?? buttonColor,
            }}
          />
        )}
      </DivImg>
      {value}
    </CommonStyledButton>
  );
};

export default CommonButton;
